import { type FormEvent, useState } from "react";
import { portalFetch } from "../api/client";
import type { PortalArtifactDto } from "../model";

const PROTOTYPE_CSP = [
	"default-src 'none'",
	"style-src 'unsafe-inline'",
	"script-src 'unsafe-inline'",
	"img-src data: blob:",
	"font-src data:",
	"connect-src 'none'",
	"frame-src 'none'",
	"object-src 'none'",
	"base-uri 'none'",
	"form-action 'none'",
].join("; ");

export function securePrototypeDocument(html: string) {
	const withoutPriorPolicy = html.replace(
		/<meta\b[^>]*http-equiv\s*=\s*["']?content-security-policy["']?[^>]*>/gi,
		"",
	);
	const policy = `<meta http-equiv="Content-Security-Policy" content="${PROTOTYPE_CSP}">`;
	if (/<head(?:\s[^>]*)?>/i.test(withoutPriorPolicy))
		return withoutPriorPolicy.replace(/<head(?:\s[^>]*)?>/i, (head) => `${head}${policy}`);
	return withoutPriorPolicy.replace(/<html(?:\s[^>]*)?>/i, (root) => `${root}<head>${policy}</head>`);
}

export function PrototypeViewer({
	projectId,
	artifact,
	onAnnotate,
	annotationDisabled = false,
}: {
	projectId: string;
	artifact: { artifactId: string; name: string };
	onAnnotate?: (requestedChange: string) => Promise<boolean>;
	annotationDisabled?: boolean;
}) {
	const [document, setDocument] = useState<string>();
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [annotationOpen, setAnnotationOpen] = useState(false);
	const [annotation, setAnnotation] = useState("");
	const [annotating, setAnnotating] = useState(false);
	const [annotationError, setAnnotationError] = useState("");

	async function show() {
		setOpen(true);
		if (document || loading) return;
		setLoading(true);
		setError("");
		try {
			const loaded = await portalFetch<PortalArtifactDto>(
				`/api/portal/v1/projects/${projectId}/artifacts/${artifact.artifactId}`,
			);
			if (loaded.mimeType !== "text/html" || !loaded.content) throw new Error("Prototype HTML is unavailable.");
			setDocument(securePrototypeDocument(loaded.content));
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : "Prototype could not be opened.");
		} finally {
			setLoading(false);
		}
	}

	async function submitAnnotation(event: FormEvent) {
		event.preventDefault();
		const requestedChange = annotation.trim();
		if (!onAnnotate || !requestedChange || annotationDisabled || annotating) return;
		setAnnotating(true);
		setAnnotationError("");
		try {
			if (!(await onAnnotate(requestedChange))) {
				setAnnotationError(
					"The annotation could not be sent. Keep it here and try again when the agent is available.",
				);
				return;
			}
			setAnnotation("");
			setAnnotationOpen(false);
			setOpen(false);
		} catch {
			setAnnotationError("The annotation could not be sent. Keep it here and try again.");
		} finally {
			setAnnotating(false);
		}
	}

	return (
		<>
			<button type="button" className="secondary prototype-open" onClick={() => void show()}>
				Open prototype
			</button>
			{open && (
				<div
					className="prototype-viewer"
					role="dialog"
					aria-modal="true"
					aria-label={`Prototype: ${artifact.name}`}
				>
					<header>
						<div>
							<span>Throwaway decision prototype</span>
							<strong>{artifact.name.split("/").at(-1)}</strong>
						</div>
						<div className="prototype-viewer-actions">
							{onAnnotate && (
								<button
									type="button"
									className="secondary"
									disabled={annotationDisabled || annotating}
									onClick={() => {
										setAnnotationOpen(true);
										setAnnotationError("");
									}}
								>
									Annotate prototype
								</button>
							)}
							<button type="button" className="secondary" onClick={() => setOpen(false)}>
								Close
							</button>
						</div>
					</header>
					<div className={`prototype-viewer-content${annotationOpen ? " has-annotation" : ""}`}>
						<div className="prototype-viewer-stage">
							{loading && <p role="status">Opening the prototype…</p>}
							{error && <p role="alert">{error}</p>}
							{document && (
								<iframe
									title={artifact.name}
									sandbox="allow-scripts"
									referrerPolicy="no-referrer"
									srcDoc={document}
								/>
							)}
						</div>
						{onAnnotate && annotationOpen && (
							<form className="prototype-annotation" onSubmit={(event) => void submitAnnotation(event)}>
								<div>
									<span>Prototype annotation</span>
									<h3>Request a focused change</h3>
									<p>
										Describe what should change in this prototype. The Portal will send only this note and the
										prototype reference to the Project Agent.
									</p>
								</div>
								<label htmlFor={`prototype-annotation-${artifact.artifactId}`}>
									What should change in this prototype?
								</label>
								<textarea
									id={`prototype-annotation-${artifact.artifactId}`}
									value={annotation}
									onChange={(event) => setAnnotation(event.currentTarget.value)}
								/>
								{annotationError && <p role="alert">{annotationError}</p>}
								<div className="prototype-annotation-actions">
									<button type="button" className="secondary" onClick={() => setAnnotationOpen(false)}>
										Back to prototype
									</button>
									<button type="submit" disabled={!annotation.trim() || annotationDisabled || annotating}>
										{annotating ? "Sending annotation…" : "Send annotation"}
									</button>
								</div>
							</form>
						)}
					</div>
				</div>
			)}
		</>
	);
}
